import React, { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { createRoot, Root } from 'react-dom/client';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import multiMonthPlugin from '@fullcalendar/multimonth';
import resourceTimelinePlugin from '@fullcalendar/resource-timeline';
import './FullCalendarWrapper.css';
import { IPlaneEvent } from '../../../../../store/reducers/scheduleReducer/scheduleReducerTypes';
import { ScheduleType } from '../../../../../proto/generated/api_entities_pb';
import { formatDate, parseStringAsUTCDate } from '../../../../../utils/helpers/dateHelpers/dateHelpers';
import { DisplayAirport, DisplayRoute } from '../../../../../utils/helpers/textDisplayHelpers/DisplayAirport';
import { defineMessages, useIntl } from 'react-intl';
import { CommonDetailsMessages } from '../../../../../intl/commonMessages/CommonDetailsMessages';
import { CommonFormMessages } from '../../../../../intl/commonMessages/CommonFormMessages';
import { EventPopover } from './EventPopover';
import { IPlaneShort } from '../../../../../store/reducers/fleetReducer/fleetReducerTypes';
import { IEventCalendar } from '../../../../../store/reducers/eventCalendarReducer/eventCalendarReducerTypes';
import { Popover } from 'antd';
import styled from 'styled-components';
import { useWindowSize } from '../../../../../hooks/useWindowSize';

const FullCalendarWrapperMessages = defineMessages({
  event: {
    id: "fullCalendar.event",
    defaultMessage: "Event",
  },
  airport: {
    id: "fullCalendar.airport",
    defaultMessage: "Airport",
  },
  route: {
    id: "fullCalendar.route",
    defaultMessage: "Route",
  },
  period: {
    id: "fullCalendar.period",
    defaultMessage: "Period",
  },
  resourceAreaTitle: {
    id: "fullCalendar.resourceAreaTitle",
    defaultMessage: "Aircraft",
  },
});

// Стили для поповера с оставшимися событиями
const MoreEventsPopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 0;
  max-width: 300px;
  max-height: 400px;
  overflow-y: auto;
`;

const MoreEventItem = styled.div<{ $backgroundColor: string; $borderColor: string }>`
  padding: 6px 12px;
  background: ${props => props.$backgroundColor};
  border-left: 3px solid ${props => props.$borderColor};
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 12px;
  font-weight: 500;
  color: #343A40;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 6px rgba(0,0,0,0.15);
  }
`;

type ViewMode = 'month' | 'year' | 'week';

interface FullCalendarWrapperProps {
  events: IPlaneEvent[];
  onEventClick: (event: IPlaneEvent) => void;
  onDateSelect: (start: Date, end: Date) => void;
  onDatesChange?: (start: Date, end: Date) => void; // Callback для изменения видимого диапазона дат
  height?: string | number;
  initialView?: string;
  initialDate?: Date;
  firstDayOfWeek?: number; // 0 = воскресенье, 1 = понедельник
  availableViewModes?: ViewMode[];
  initialViewMode?: ViewMode;
  planeList?: IPlaneShort[];
  headerLeftContent?: React.ReactNode; // Custom content for the left side of the header toolbar
  headerRightContent?: React.ReactNode; // Custom content for the right side of the header toolbar
}

export const FullCalendarWrapper: React.FC<FullCalendarWrapperProps> = ({
  events,
  onEventClick,
  onDateSelect,
  onDatesChange,
  height = 'auto',
  initialView = 'dayGridMonth',
  initialDate = new Date(),
  firstDayOfWeek = 1, // по умолчанию понедельник
  availableViewModes,
  initialViewMode,
  planeList: planeListProp,
  headerLeftContent,
  headerRightContent,
}) => {
  const calendarRef = useRef<FullCalendar>(null);
  const intl = useIntl();
  const { width } = useWindowSize(); // Отслеживаем изменение ширины экрана для пересчета позиционирования событий
  
  const resolvedViewModes = useMemo<ViewMode[]>(() => {
    const fallback: ViewMode[] = ['month', 'year'];
    if (!availableViewModes || availableViewModes.length === 0) {
      return fallback;
    }

    return availableViewModes;
  }, [availableViewModes]);

  const resolvedInitialViewMode = useMemo<ViewMode>(() => {
    if (initialViewMode && resolvedViewModes.includes(initialViewMode)) {
      return initialViewMode;
    }

    return resolvedViewModes[0];
  }, [initialViewMode, resolvedViewModes]);

  const [viewMode, setViewMode] = useState<ViewMode>(resolvedInitialViewMode);
  const [popoverVisible, setPopoverVisible] = useState(false);
  const [selectedEventForPopover, setSelectedEventForPopover] = useState<IPlaneEvent | null>(null);
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLElement | null>(null);
  const popoverAnchorRef = useRef<HTMLDivElement>(null);
  const isMouseOnPopoverRef = useRef<boolean>(false);
  const closeTimeoutRef = useRef<number | null>(null);
  
  // Состояние для поповера с оставшимися событиями (для Event Calendar)
  const [moreEventsPopoverVisible, setMoreEventsPopoverVisible] = useState(false);
  const [moreEventsPopoverAnchor, setMoreEventsPopoverAnchor] = useState<HTMLElement | null>(null);
  const [moreEventsList, setMoreEventsList] = useState<IEventCalendar[]>([]);
  const moreEventsPopoverCloseTimeoutRef = useRef<number | null>(null);

  const planeList = useMemo(() => {
    if (planeListProp && planeListProp.length > 0) {
      return [...planeListProp].sort((a, b) =>
        a.registrationNumber.localeCompare(b.registrationNumber, intl.locale, { numeric: true, sensitivity: 'base' })
      );
    }

    const planesMap = new Map<number, IPlaneShort>();

    events.forEach((event) => {
      const plane = (event as any).plane as IPlaneShort | undefined;
      if (plane?.id && plane?.registrationNumber) {
        planesMap.set(plane.id, {
          id: plane.id,
          registrationNumber: plane.registrationNumber,
        });
      }
    });

    return Array.from(planesMap.values()).sort((a, b) =>
      a.registrationNumber.localeCompare(b.registrationNumber, intl.locale, { numeric: true, sensitivity: 'base' })
    );
  }, [events, intl.locale, planeListProp]);

  const hasUnassignedEvents = useMemo(
    () =>
      events.some((event) => {
        const plane = (event as any).plane as IPlaneShort | undefined;
        return !(plane?.id);
      }),
    [events]
  );

  // Используем planeListProp (без сортировки) для создания ресурсов, если он передан
  // Это гарантирует, что порядок ресурсов в FullCalendar соответствует порядку списка самолетов слева
  const resourcesListForTimeline = useMemo(() => {
    if (planeListProp && planeListProp.length > 0) {
      return planeListProp;
    }
    return planeList;
  }, [planeListProp, planeList]);

  const timelineResources = useMemo(() => {
    const resources = resourcesListForTimeline.map((plane) => ({
      id: plane.id.toString(),
      title: plane.registrationNumber,
    }));

    if (hasUnassignedEvents) {
      resources.push({
        id: 'unassigned',
        title: '—',
      });
    }

    return resources;
  }, [hasUnassignedEvents, resourcesListForTimeline]);

  // Константы и вспомогательные функции для работы с датами
    const MS_IN_DAY = 24 * 60 * 60 * 1000;

  const getWeekStart = useCallback((date: Date) => {
      const result = new Date(date);
      result.setHours(0, 0, 0, 0);
      const currentDay = result.getDay();
      const diff = (currentDay - firstDayOfWeek + 7) % 7;
      result.setDate(result.getDate() - diff);
      return result;
  }, [firstDayOfWeek]);

  const getWeekEnd = useCallback((date: Date) => {
      const weekStart = getWeekStart(date);
      return new Date(weekStart.getTime() + 7 * MS_IN_DAY);
  }, [getWeekStart]);

  // Преобразуем данные IPlaneEvent в формат FullCalendar
  const calendarEvents = useMemo(() => {
    // Создаем Map для быстрого поиска индекса самолета по ID
    const planeIndexMap = new Map<number, number>();
    planeList.forEach((plane, index) => {
      planeIndexMap.set(plane.id, index);
    });

    // Конвертируем UTC дату в локальную дату с теми же компонентами для FullCalendar
    // FullCalendar интерпретирует Date как локальное время, поэтому нужно создать Date
    // с локальными компонентами, соответствующими UTC компонентам
    const convertUTCToLocalDate = (input: Date | string): Date | null => {
      let date: Date;
      
      if (input instanceof Date) {
        // Если это Date объект, извлекаем UTC компоненты и создаем локальный Date
        date = new Date(
          input.getUTCFullYear(),
          input.getUTCMonth(),
          input.getUTCDate(),
          input.getUTCHours(),
          input.getUTCMinutes(),
          input.getUTCSeconds()
        );
      } else {
        // Если это строка, парсим её и создаем локальный Date
        const dateStr = String(input).trim();
        
        // Проверяем формат "YYYY-MM-DD HH:mm:ss"
        const dateTimeMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})\s+(\d{2}):(\d{2}):(\d{2})$/);
        if (dateTimeMatch) {
          const [, year, month, day, hour, minute, second] = dateTimeMatch.map(Number);
          date = new Date(year, month - 1, day, hour, minute, second);
        } else {
          // Проверяем формат "YYYY-MM-DD"
          const dateMatch = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (dateMatch) {
            const [, year, month, day] = dateMatch.map(Number);
            date = new Date(year, month - 1, day, 0, 0, 0);
          } else {
            // Fallback: пытаемся парсить как есть
            date = new Date(input);
          }
        }
      }
      
      return Number.isFinite(date.getTime()) ? date : null;
    };

    const splitEventByDays = (start: Date, end: Date) => {
      const segments: Array<{ start: Date; end: Date }> = [];

      if (end <= start) {
        segments.push({
          start: new Date(start),
          end: new Date(start.getTime()),
        });
        return segments;
      }

      // Начинаем с первого дня (используем локальные методы, так как даты уже конвертированы)
      let currentDay = new Date(start);
      currentDay.setHours(0, 0, 0, 0);
      
      // Конец события (используем локальные методы)
      const endDay = new Date(end);
      endDay.setHours(0, 0, 0, 0);

      // Проверяем, заканчивается ли событие ровно в 00:00:00.000
      // Если да, то не показываем сегмент для этого дня (событие фактически закончилось в предыдущий день)
      const endTime = end.getHours() * 3600000 + end.getMinutes() * 60000 + end.getSeconds() * 1000 + end.getMilliseconds();
      const shouldExcludeLastDay = endTime === 0;

      // Если событие заканчивается в 00:00:00, исключаем последний день из цикла
      const effectiveEndDay = shouldExcludeLastDay 
        ? new Date(endDay.getTime() - 24 * 60 * 60 * 1000) // Вычитаем один день
        : endDay;

      while (currentDay <= effectiveEndDay) {
        const dayStart = new Date(currentDay);
        const dayEnd = new Date(currentDay);
        dayEnd.setHours(23, 59, 59, 999);

        // Определяем границы сегмента для текущего дня
        let segmentStart: Date;
        let segmentEnd: Date;

        const isFirstDay = segments.length === 0;
        const isLastDay = currentDay.getTime() === effectiveEndDay.getTime();
        const isSingleDay = isFirstDay && isLastDay;

        if (isSingleDay) {
          // Событие начинается и заканчивается в один день - используем точное время начала и конца
          segmentStart = new Date(start);
          segmentEnd = new Date(end);
        } else if (isFirstDay) {
          // Первый день - начинаем с точного времени начала события, заканчиваем концом дня
          segmentStart = new Date(start);
          segmentEnd = new Date(dayEnd);
        } else if (isLastDay) {
          // Последний день - начинаем с начала дня
          segmentStart = new Date(dayStart);
          // Если событие заканчивается в 00:00:00, мы уже исключили этот день из цикла,
          // поэтому для последнего дня в цикле используем конец дня
          // Иначе используем точное время конца события
          segmentEnd = shouldExcludeLastDay 
            ? new Date(dayEnd) 
            : new Date(end);
        } else {
          // Промежуточные дни - весь день
          segmentStart = new Date(dayStart);
          segmentEnd = new Date(dayEnd);
        }

        segments.push({
          start: segmentStart,
          end: segmentEnd,
        });

        // Переходим к следующему дню (используем локальные методы)
        currentDay.setDate(currentDay.getDate() + 1);
      }

      return segments;
    };


    return events.flatMap((event) => {
      // Проверяем, является ли это событием Event Calendar
      // Если есть extendedProps.event, значит это событие Event Calendar, пропускаем логику Schedule
      const eventAsAny = event as any;
      const eventCalendar = eventAsAny.extendedProps?.event;
      if (eventCalendar && eventCalendar.eventCalendarId) {
        // Для событий Event Calendar конвертируем UTC даты в локальные для FullCalendar
        const eventStart = convertUTCToLocalDate(event.dateFrom);
        const eventEnd = convertUTCToLocalDate(event.dateTo);

        if (!eventStart || !eventEnd) {
          return [];
        }

        const effectiveEnd =
          eventEnd > eventStart ? eventEnd : new Date(eventStart.getTime());

        const segments = viewMode === 'month'
          ? splitEventByDays(eventStart, effectiveEnd)
          : viewMode === 'year'
          ? splitEventByDays(eventStart, effectiveEnd)
          : [{
              start: new Date(eventStart),
              end: new Date(effectiveEnd),
            }];

        return segments.map((segment, segmentIndex) => ({
          id: segments.length > 1 ? `${event.id}-${segmentIndex}` : event.id.toString(),
          title: eventAsAny.title || event.eventName || '',
          start: segment.start,
          end: segment.end,
          backgroundColor: eventAsAny.backgroundColor,
          borderColor: eventAsAny.borderColor,
          resourceId: eventAsAny.resourceId || 'unassigned',
          extendedProps: {
            ...eventAsAny.extendedProps,
            event: eventCalendar,
            segmentStartTs: segment.start.getTime(),
            segmentEndTs: segment.end.getTime(),
            segmentIndex,
            segmentCount: segments.length,
          },
        }));
      }

      // Логика для Schedule событий
      const plane = event.plane;

      // Конвертируем UTC даты в локальные для FullCalendar
      const eventStart = convertUTCToLocalDate(event.dateFrom);
      const eventEnd = convertUTCToLocalDate(event.dateTo);

      if (!eventStart || !eventEnd) {
        return [];
      }

      const effectiveEnd =
        eventEnd > eventStart ? eventEnd : new Date(eventStart.getTime());

      // Получаем тип события
      const eventTypeText = event.eventType === ScheduleType.EMPTY_LEG
        ? intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)
        : event.eventType === ScheduleType.ORDER_REQUEST
          ? intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)
          : event.eventType === ScheduleType.CUSTOM
            ? intl.formatMessage(CommonFormMessages.eventTypeCustomRadio)
            : event.eventType === ScheduleType.MAINTENANCE
              ? intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio)
              : event.eventType === ScheduleType.OTHER
                ? intl.formatMessage(CommonFormMessages.eventTypeParkingRadio)
                : intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio);

      // Формируем название: [номер самолёта] - [тип события] - [название события, если есть]
      const titleParts = [
        plane?.registrationNumber ?? '—',
        eventTypeText
      ];
      
      // Добавляем название события, если оно есть
      if (event.eventName) {
        titleParts.push(event.eventName);
      }
      
      const title = titleParts.join(' - ');

      // Убеждаемся, что resourceId точно соответствует ID ресурса
      // Ресурсы создаются с id: plane.id.toString(), поэтому используем то же преобразование
      // plane обязателен в IPlaneEvent, поэтому не может быть undefined
      // Важно: resourceId должен точно совпадать с id ресурса для правильной привязки
      const resourceId = plane.id.toString();
      
      // Проверяем, что самолет есть в planeList (для событий без привязки используем 'unassigned')
      const planeIndex = planeIndexMap.get(plane.id);
      if (planeIndex === undefined) {
        // Если самолет не найден в planeList, используем 'unassigned'
        // Но это не должно происходить для нормальных событий Schedule
      }

      // Определяем цвет события в зависимости от типа
      let backgroundColor = '#3788d8'; // синий по умолчанию
      if (event.eventType === ScheduleType.EMPTY_LEG) {
        backgroundColor = '#ff6b6b'; // красный для пустых рейсов
      } else if (event.eventType === ScheduleType.ORDER_REQUEST) {
        backgroundColor = '#51cf66'; // зеленый для заказов
      } else if (event.eventType === ScheduleType.CUSTOM) {
        backgroundColor = '#ffd43b'; // желтый для кастомных событий
      } else if (event.eventType === ScheduleType.MAINTENANCE) {
        backgroundColor = '#ff9800'; // оранжевый для технического обслуживания
      } else if (event.eventType === ScheduleType.OTHER) {
        backgroundColor = '#9e9e9e'; // серый для других событий (парковка)
      }

      const segments = viewMode === 'month'
        ? splitEventByDays(eventStart, effectiveEnd)
        : [{
            start: new Date(eventStart),
            end: new Date(effectiveEnd),
          }];

      return segments.map((segment, segmentIndex) => ({
        id: segments.length > 1 ? `${event.id}-${segmentIndex}` : event.id.toString(),
        title,
        start: segment.start,
        end: segment.end,
        backgroundColor,
        borderColor: backgroundColor,
        resourceId,
        extendedProps: {
          planeEvent: event,
          segmentStartTs: segment.start.getTime(),
          segmentEndTs: segment.end.getTime(),
          segmentIndex,
          segmentCount: segments.length,
        },
      }));
    });
  }, [events, firstDayOfWeek, intl, viewMode, planeList]);

  const handleEventClick = (clickInfo: any) => {
    // Проверяем, является ли это событием Event Calendar
    const eventCalendar = clickInfo.event.extendedProps?.event;
    if (eventCalendar && eventCalendar.eventCalendarId) {
      // Для событий Event Calendar передаем сам объект eventCalendar
      onEventClick(eventCalendar);
      return;
    }
    
    // Для Schedule событий передаем planeEvent
    const planeEvent = clickInfo.event.extendedProps?.planeEvent;
    if (planeEvent) {
      // Используем те же Date объекты, что и в календаре для правильного отображения времени
      // Берем segmentStartTs и segmentEndTs, которые используются для расчета позиции в календаре
      const segmentStartTs = clickInfo.event.extendedProps?.segmentStartTs;
      const segmentEndTs = clickInfo.event.extendedProps?.segmentEndTs;
      
      // Создаем копию события с правильными датами для отображения в модалке
      const eventForModal: IPlaneEvent = {
        ...planeEvent,
        dateFrom: Number.isFinite(segmentStartTs) 
          ? new Date(segmentStartTs as number) 
          : planeEvent.dateFrom,
        dateTo: Number.isFinite(segmentEndTs) 
          ? new Date(segmentEndTs as number) 
          : planeEvent.dateTo,
      };
      
      onEventClick(eventForModal);
    }
  };

  const handleDateSelect = (selectInfo: any) => {
    onDateSelect(selectInfo.start, selectInfo.end);
  };

  const clearCloseTimeout = useCallback(() => {
    if (closeTimeoutRef.current !== null) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const hidePopover = useCallback(() => {
    clearCloseTimeout();
    setPopoverVisible(false);
    isMouseOnPopoverRef.current = false;
    // Очищаем данные с небольшой задержкой для Safari
    setTimeout(() => {
      setSelectedEventForPopover(null);
      setPopoverAnchorEl(null);
    }, 100);
  }, [clearCloseTimeout]);

  const handleEventMouseEnter = (mouseEnterInfo: any) => {
    // Проверяем, является ли это событием Event Calendar
    // Если да, то не показываем поповер (он предназначен только для Schedule событий)
    const eventCalendar = mouseEnterInfo.event.extendedProps?.event;
    if (eventCalendar && eventCalendar.eventCalendarId) {
      return;
    }
    
    const planeEvent = mouseEnterInfo.event.extendedProps.planeEvent;
    const eventEl = mouseEnterInfo.el;
    
    if (!eventEl || !planeEvent) {
      return;
    }
    
    // Отменяем закрытие, если оно было запланировано
    clearCloseTimeout();
    isMouseOnPopoverRef.current = false;
    
    // Используем исходные UTC даты из planeEvent для поповера
    // Эти даты были правильно распарсены из UTC строк с бэкенда через parseStringAsUTCDate
    // Не используем segmentStartTs/segmentEndTs, так как они содержат локальные даты для календаря
    const eventForPopover: IPlaneEvent = {
      ...planeEvent,
      // Используем исходные UTC даты, которые были правильно распарсены
      dateFrom: planeEvent.dateFrom,
      dateTo: planeEvent.dateTo,
    };
    
    // Сохраняем данные события
    setPopoverAnchorEl(eventEl as HTMLElement);
    setSelectedEventForPopover(eventForPopover);
  };

  const handleEventMouseLeave = () => {
    // В Safari используем задержку, чтобы дать время курсору перейти на поповер
    clearCloseTimeout();
    closeTimeoutRef.current = window.setTimeout(() => {
      // Закрываем только если курсор не на поповере
      if (!isMouseOnPopoverRef.current) {
        hidePopover();
      }
    }, 150);
  };

  // Устанавливаем позицию и показываем поповер когда данные готовы
  useEffect(() => {
    if (popoverAnchorEl && popoverAnchorRef.current && selectedEventForPopover) {
      // На таймлайне используем дочерний элемент (само событие) для позиционирования,
      // так как контейнер .fc-timeline-event имеет прозрачный фон и может не иметь реальной высоты
      let elementForPositioning = popoverAnchorEl as HTMLElement;
      if (viewMode === 'week') {
        const customEventElement = popoverAnchorEl.querySelector('.fc-custom-event--timeline') as HTMLElement;
        if (customEventElement) {
          elementForPositioning = customEventElement;
        }
      }
      
      // Устанавливаем позицию поповера
      const rect = elementForPositioning.getBoundingClientRect();
      popoverAnchorRef.current.style.position = 'fixed';
      popoverAnchorRef.current.style.left = `${rect.left + rect.width / 2}px`;
      popoverAnchorRef.current.style.top = `${rect.top - 8}px`; // Поднимаем якорь выше для лучшего позиционирования
      popoverAnchorRef.current.style.width = '1px';
      popoverAnchorRef.current.style.height = '1px';
      popoverAnchorRef.current.style.pointerEvents = 'none';
      
      // Показываем поповер после установки позиции
      requestAnimationFrame(() => {
        setPopoverVisible(true);
      });
    }
  }, [popoverAnchorEl, selectedEventForPopover, viewMode]);

  // Добавляем обработчики на поповер, чтобы он оставался открытым при наведении
  useEffect(() => {
    if (!popoverVisible) {
      return;
    }

    let popoverElement: Element | null = null;
    let cleanup: (() => void) | null = null;

    // Используем небольшую задержку для Safari, чтобы поповер успел отрендериться
    const timeoutId = setTimeout(() => {
      popoverElement = document.querySelector('.schedule-event-popover');
      if (popoverElement) {
        const handleMouseEnter = () => {
          clearCloseTimeout();
          isMouseOnPopoverRef.current = true;
        };
        const handleMouseLeave = () => {
          isMouseOnPopoverRef.current = false;
          hidePopover();
        };
        
        popoverElement.addEventListener('mouseenter', handleMouseEnter);
        popoverElement.addEventListener('mouseleave', handleMouseLeave);
        
        cleanup = () => {
          popoverElement?.removeEventListener('mouseenter', handleMouseEnter);
          popoverElement?.removeEventListener('mouseleave', handleMouseLeave);
        };
      }
    }, 50);
    
    return () => {
      clearTimeout(timeoutId);
      if (cleanup) {
        cleanup();
      }
    };
  }, [popoverVisible, hidePopover, clearCloseTimeout]);

  // Обновляем позицию Popover при скролле или изменении размера окна
  useEffect(() => {
    if (popoverAnchorEl && popoverAnchorRef.current && popoverVisible) {
      const updatePosition = () => {
        if (popoverAnchorEl && popoverAnchorRef.current) {
          // На таймлайне используем дочерний элемент (само событие) для позиционирования
          let elementForPositioning = popoverAnchorEl as HTMLElement;
          if (viewMode === 'week') {
            const customEventElement = popoverAnchorEl.querySelector('.fc-custom-event--timeline') as HTMLElement;
            if (customEventElement) {
              elementForPositioning = customEventElement;
            }
          }
          
          const rect = elementForPositioning.getBoundingClientRect();
          popoverAnchorRef.current.style.position = 'fixed';
          popoverAnchorRef.current.style.left = `${rect.left + rect.width / 2}px`;
          popoverAnchorRef.current.style.top = `${rect.top - 8}px`; // Поднимаем якорь выше для лучшего позиционирования
          popoverAnchorRef.current.style.width = '1px';
          popoverAnchorRef.current.style.height = '1px';
          popoverAnchorRef.current.style.pointerEvents = 'none';
        }
      };

      // Используем debounce для обновления позиции при скролле/ресайзе
      let rafId: number | null = null;
      const debouncedUpdatePosition = () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        rafId = requestAnimationFrame(() => {
          updatePosition();
          rafId = null;
        });
      };

      const handleScroll = () => debouncedUpdatePosition();
      const handleResize = () => debouncedUpdatePosition();

      window.addEventListener('scroll', handleScroll, true);
      window.addEventListener('resize', handleResize);

      return () => {
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        window.removeEventListener('scroll', handleScroll, true);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [popoverAnchorEl, popoverVisible, viewMode]);

  // Закрываем Popover при клике вне его области
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (popoverVisible && popoverAnchorEl) {
        const target = event.target as HTMLElement;
        // Проверяем, что клик не по самому Popover и не по событию
        if (
          !popoverAnchorEl.contains(target) &&
          !target.closest('.ant-popover') &&
          !target.closest('.ant-popover-inner')
        ) {
          hidePopover();
        }
      }
    };

    if (popoverVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [popoverVisible, popoverAnchorEl, hidePopover]);

  // Функция для получения ширины ячейки дня
  const getDayCellWidth = () => {
    const monthCell = document.querySelector('.fc-day');
    if (monthCell) {
      return monthCell.getBoundingClientRect().width;
    }

    const weekCell = document.querySelector('.fc-timegrid-col-frame');
    if (weekCell) {
      return weekCell.getBoundingClientRect().width;
    }
  };

  // Функция для получения высоты ячейки дня
  const getDayCellHeight = () => {
    const monthCell = document.querySelector('.fc-daygrid-day');
    if (monthCell) {
      return monthCell.getBoundingClientRect().height;
    }

    const weekSlot = document.querySelector('.fc-timegrid-slot');
    if (weekSlot) {
      return weekSlot.getBoundingClientRect().height;
    }
  };

  // Пересчитываем позиционирование событий при изменении ширины экрана
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      // Вызываем updateSize для пересчета размеров и позиционирования событий
      calendarApi.updateSize();
    }
  }, [width]);

  // Пересчитываем позиции событий после рендеринга на основе реального порядка в DOM
  useEffect(() => {
    if (viewMode !== 'month') {
      return;
    }

    const recalculateEventPositions = () => {
      // Находим все ячейки дней
      const dayCells = document.querySelectorAll('.fc-daygrid-day');
      
      dayCells.forEach((dayCell) => {
        const dayEventsContainer = dayCell.querySelector('.fc-daygrid-day-events');
        if (!dayEventsContainer) return;

        // Находим все видимые события в этом дне (исключаем скрытые и "more link")
        const allEventElements = Array.from(dayEventsContainer.querySelectorAll('.fc-custom-event'));
        const visibleEvents = allEventElements.filter((el) => {
          const style = window.getComputedStyle(el as HTMLElement);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });

        if (visibleEvents.length === 0) return;

        // Сортируем события по их порядку в DOM (порядок отображения)
        // Используем getBoundingClientRect для определения реальной позиции
        const sortedEvents = visibleEvents
          .map((el) => {
            const rect = (el as HTMLElement).getBoundingClientRect();
            const containerRect = dayEventsContainer.getBoundingClientRect();
            // Вычисляем относительную позицию внутри контейнера
            const relativeTop = rect.top - containerRect.top;
            return { el, top: relativeTop };
          })
          .sort((a, b) => a.top - b.top);

        // Получаем высоту события из первого элемента или используем дефолт
        const firstEvent = sortedEvents[0]?.el as HTMLElement;
        if (!firstEvent) return;
        
        const eventHeight = parseFloat(window.getComputedStyle(firstEvent).height) || 16.1;

        // Пересчитываем позиции: первое событие с top: 0, второе с отступом от первого и т.д.
        sortedEvents.forEach(({ el }, index) => {
          const newTop = index * eventHeight;
          (el as HTMLElement).style.top = `${newTop}px`;
        });
      });
    };

    // Вызываем после небольшой задержки, чтобы DOM успел обновиться
    const timeoutId = setTimeout(() => {
      recalculateEventPositions();
    }, 100);

    // Также вызываем при изменении событий
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const handleEventsSet = () => {
        setTimeout(recalculateEventPositions, 100);
      };
      
      calendarApi.on('eventsSet', handleEventsSet);
      calendarApi.on('viewDidMount' as any, handleEventsSet);

      return () => {
        clearTimeout(timeoutId);
        calendarApi.off('eventsSet', handleEventsSet);
        calendarApi.off('viewDidMount' as any, handleEventsSet);
      };
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [viewMode, calendarEvents, width]);

  // Позиционирование событий на таймлайне по индексу самолета с учетом наложений
  useEffect(() => {
    if (viewMode !== 'week') {
      return;
    }

    const positionTimelineEvents = () => {
      // Находим все контейнеры событий на таймлайне (fc-timeline-event-harness)
      const timelineEventHarnesses = document.querySelectorAll('.fc-timeline-event-harness');
      
      // Группируем события по индексу самолета
      const eventsByPlaneIndex = new Map<number, Array<{harness: HTMLElement; event: HTMLElement; start: number; end: number}>>();
      
      timelineEventHarnesses.forEach((harnessEl) => {
        const harness = harnessEl as HTMLElement;
        const customEvent = harness.querySelector('.fc-custom-event--timeline[data-plane-index]') as HTMLElement;
        
        if (!customEvent) return;
        
        const planeIndex = customEvent.getAttribute('data-plane-index');
        if (planeIndex === null || planeIndex === '-1') return;
        
        const index = parseInt(planeIndex, 10);
        if (isNaN(index) || index < 0) return;
        
        // Получаем временные границы события из позиции в DOM
        const rect = harness.getBoundingClientRect();
        const timelineBody = harness.closest('.fc-timeline-body');
        if (!timelineBody) return;
        
        const timelineBodyRect = timelineBody.getBoundingClientRect();
        const left = rect.left - timelineBodyRect.left;
        const width = rect.width;
        const start = left;
        const end = left + width;
        
        if (!eventsByPlaneIndex.has(index)) {
          eventsByPlaneIndex.set(index, []);
        }
        
        eventsByPlaneIndex.get(index)!.push({
          harness,
          event: customEvent,
          start,
          end
        });
      });
      
      // Для каждого самолета позиционируем события
      eventsByPlaneIndex.forEach((events, planeIndex) => {
        // Базовая позиция строки ресурса (первый самолет = 0px, второй = 102px и т.д.)
        const baseTop = planeIndex * 102;
        
        // Сортируем события по времени начала (слева направо)
        events.sort((a, b) => a.start - b.start);
        
        // Проверяем наложения и позиционируем
        const positionedEvents: Array<{harness: HTMLElement; top: number; start: number; end: number}> = [];
        const eventHeight = 20.4; // Высота события в пикселях
        
        events.forEach(({harness, start, end}) => {
          // Ищем наложения с уже позиционированными событиями
          let additionalOffset = 0;
          
          for (const positioned of positionedEvents) {
            // Проверяем наложение по времени (если события перекрываются по горизонтали)
            const hasOverlap = !(end <= positioned.start || start >= positioned.end);
            
            if (hasOverlap) {
              // Есть наложение - нужно сдвинуть вниз
              // Вычисляем, на сколько нужно сдвинуть, чтобы не было наложения
              const currentOffset = positioned.top - baseTop;
              additionalOffset = Math.max(additionalOffset, currentOffset + eventHeight);
            }
          }
          
          // Устанавливаем финальную позицию: базовая позиция самолета + дополнительный отступ при наложении
          const finalTop = baseTop + additionalOffset;
          harness.style.setProperty('top', `${finalTop}px`, 'important');
          
          positionedEvents.push({
            harness,
            top: finalTop,
            start,
            end
          });
        });
      });
    };

    // Вызываем после небольшой задержки, чтобы DOM успел обновиться
    const timeoutId = setTimeout(() => {
      positionTimelineEvents();
    }, 100);

    // Также вызываем при изменении событий и после рендеринга
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      const handleEventsSet = () => {
        setTimeout(positionTimelineEvents, 100);
      };
      
      calendarApi.on('eventsSet', handleEventsSet);
      calendarApi.on('viewDidMount' as any, handleEventsSet);

      return () => {
        clearTimeout(timeoutId);
        calendarApi.off('eventsSet', handleEventsSet);
        calendarApi.off('viewDidMount' as any, handleEventsSet);
      };
    }

    return () => {
      clearTimeout(timeoutId);
    };
  }, [viewMode, calendarEvents, resourcesListForTimeline]);

  const eventContent = useCallback((eventInfo: any) => {
    // Поддерживаем оба формата: planeEvent (для Schedule) и event (для Event Calendar)
    // ВАЖНО: сначала проверяем eventCalendar, так как для Event Calendar создаются фиктивные planeEvent
    const eventCalendar = eventInfo.event.extendedProps?.event as IEventCalendar | undefined;
    const planeEvent = eventInfo.event.extendedProps?.planeEvent;

    // Если это событие из Event Calendar - используем его данные
    if (eventCalendar && eventCalendar.eventCalendarId) {
      // Используем color из eventCalendar, который приходит с бекенда
      const eventColor = eventCalendar.color || '#1890FF';
      // Используем eventName из eventCalendar, который приходит с бекенда
      const eventName = eventCalendar.eventName || eventInfo.event.title || '';
      
      // Преобразуем hex в rgba с прозрачностью 0.5 для фона (50% opacity)
      const hex = eventColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const backgroundColor = `rgba(${r}, ${g}, ${b}, 0.5)`;
      const borderColor = eventColor;

      if (viewMode === 'week') {
        // Для событий Event Calendar также нужно учитывать позицию самолета
        // Получаем resourceId из события
        const resourceId = eventInfo.event.resourceId;
        
        let planeIndex = -1;
        let topOffset = 0;

        // Пытаемся найти самолет по resourceId
        // Используем resourcesListForTimeline, чтобы порядок совпадал с порядком ресурсов
        if (resourceId && resourceId !== 'unassigned') {
          const planeId = parseInt(resourceId, 10);
          if (!isNaN(planeId)) {
            planeIndex = resourcesListForTimeline.findIndex(p => p.id === planeId);
            if (planeIndex !== -1) {
              // Рассчитываем topOffset: первый самолет (index 0) = 0px, второй (index 1) = 102px, третий (index 2) = 204px и т.д.
              topOffset = planeIndex * 102;
            }
          }
        }

        // Используем data-plane-index для CSS позиционирования
        return {
          html: `
            <div class="fc-custom-event fc-custom-event--timeline" data-plane-index="${planeIndex}" style="height: 20.4px; min-height: 20.4px; background: ${backgroundColor}; border-left: 3px solid ${borderColor};">
              <div class="fc-event-text">
                ${eventName}
              </div>
            </div>
          `,
        };
      }

      if (viewMode === 'year') {
        const currentDate = new Date(eventInfo.event.start);
        const dayKey = currentDate.toISOString().split('T')[0];
        const dayStart = new Date(currentDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);

        // Фильтруем события, которые перекрывают текущий день
        const dayEvents = calendarEvents.filter(event => {
          const eventStart = new Date(event.start);
          const eventEnd = new Date(event.end || eventStart);
          
          // Событие перекрывает день, если оно начинается до конца дня И заканчивается после начала дня
          return eventStart <= dayEnd && eventEnd >= dayStart;
        }).sort((a, b) => {
          // Сортируем по времени начала события
          const aStart = new Date(a.start).getTime();
          const bStart = new Date(b.start).getTime();
          return aStart - bStart;
        });

        const eventIndex = dayEvents.findIndex(event => event.id === eventInfo.event.id);
        
        // Ограничиваем отображение до 3 событий в день для режима year
        if (eventIndex >= 3) {
          return { html: '<div style="display: none;"></div>' };
        }

        const cellWidth = getDayCellWidth();

          // Парсим даты события как UTC для расчета ширины
          const eventStartDate = typeof eventCalendar.dateFrom === 'string' 
            ? parseStringAsUTCDate(eventCalendar.dateFrom)
            : (eventCalendar.dateFrom && typeof eventCalendar.dateFrom === 'object' && 'getTime' in eventCalendar.dateFrom)
              ? eventCalendar.dateFrom as Date
              : new Date(eventCalendar.dateFrom as any);
          const eventEndDate = typeof eventCalendar.dateTo === 'string'
            ? parseStringAsUTCDate(eventCalendar.dateTo)
            : (eventCalendar.dateTo && typeof eventCalendar.dateTo === 'object' && 'getTime' in eventCalendar.dateTo)
              ? eventCalendar.dateTo as Date
              : new Date(eventCalendar.dateTo as any);
          
        // Используем сегмент события, который мы создали для текущего дня
        // eventInfo.event.start и eventInfo.event.end - это даты сегмента для текущего дня
        // Сегменты уже разбиты по дням с правильными границами:
        // - Первый день: от точного времени начала до конца дня (23:59:59)
        // - Промежуточные дни: от начала дня (00:00:00) до конца дня (23:59:59)
        // - Последний день: от начала дня (00:00:00) до точного времени конца
        const segmentStartDate = new Date(eventInfo.event.start);
        const segmentEndDate = new Date(eventInfo.event.end || segmentStartDate);
        
        // Границы текущего дня
        const currentDayStart = new Date(currentDate);
        currentDayStart.setHours(0, 0, 0, 0);
        const currentDayEnd = new Date(currentDayStart);
        currentDayEnd.setHours(23, 59, 59, 999);

        const dayCellHeight = getDayCellHeight();
        const eventHeight = dayCellHeight ? dayCellHeight / 5 : 12;
        const topValue = eventIndex * eventHeight;

        let styleValue = '';
        
        if (cellWidth) {
          const cellOneHourWidth = cellWidth / 24;
          
          // Вычисляем позицию начала события в текущем дне
          const dayStartTime = currentDayStart.getTime();
          const startTotalHours = Math.max(
            (segmentStartDate.getTime() - dayStartTime) / (1000 * 60 * 60),
            0
          );
          const leftPos = startTotalHours * cellOneHourWidth;
          
          // Вычисляем ширину события на основе длительности сегмента
          const durationMs = segmentEndDate.getTime() - segmentStartDate.getTime();
          const durationHoursExact = Math.max(durationMs / (1000 * 60 * 60), 0);
          let widthValue = durationHoursExact * cellOneHourWidth;
          
          // Ограничиваем ширину, чтобы событие не выходило за границы ячейки дня
          const maxWidth = cellWidth - leftPos;
          widthValue = Math.min(widthValue, maxWidth);

          styleValue = `left: ${leftPos}px; width: ${widthValue}px; top: ${topValue}px; height: ${eventHeight}px; background: ${backgroundColor}; border-left: 3px solid ${borderColor};`;
        } else {
          // Fallback если cellWidth не определен
          styleValue = `left: 0; right: 0; top: ${topValue}px; height: ${eventHeight}px; background: ${backgroundColor}; border-left: 3px solid ${borderColor};`;
        }

        return {
          html: `
            <div class="fc-custom-event fc-year-event" style="${styleValue}">
              <div class="fc-event-text">
                ${eventName}
              </div>
            </div>
          `
        };
      }

      // Для month view
      const segmentStartTs = eventInfo.event.extendedProps.segmentStartTs as number | undefined;
      const segmentEndTs = eventInfo.event.extendedProps.segmentEndTs as number | undefined;

      // Парсим даты события как UTC
      const startDate = typeof eventCalendar.dateFrom === 'string'
        ? parseStringAsUTCDate(eventCalendar.dateFrom)
        : (eventCalendar.dateFrom && typeof eventCalendar.dateFrom === 'object' && 'getTime' in eventCalendar.dateFrom)
          ? eventCalendar.dateFrom as Date
          : new Date(eventCalendar.dateFrom as any);
      const endDate = typeof eventCalendar.dateTo === 'string'
        ? parseStringAsUTCDate(eventCalendar.dateTo)
        : (eventCalendar.dateTo && typeof eventCalendar.dateTo === 'object' && 'getTime' in eventCalendar.dateTo)
          ? eventCalendar.dateTo as Date
          : new Date(eventCalendar.dateTo as any);

      const segmentStartDate = Number.isFinite(segmentStartTs) ? new Date(segmentStartTs as number) : startDate;
      const segmentEndDate = Number.isFinite(segmentEndTs) ? new Date(segmentEndTs as number) : endDate;

      const durationMs = segmentEndDate.getTime() - segmentStartDate.getTime();
      const durationHoursExact = Math.max(durationMs / (1000 * 60 * 60), 0);
      const cellWidth = getDayCellWidth();
      let leftValue = 0;
      let widthValue = 0;
      let topValue = 0;

      if (cellWidth) {
        const cellOneHourWidth = cellWidth / 24;
        const segmentIndex = typeof eventInfo.event.extendedProps.segmentIndex === 'number'
          ? eventInfo.event.extendedProps.segmentIndex
          : 0;
        const isContinuation = segmentIndex > 0 || segmentStartDate.getTime() > startDate.getTime();

        const dayStart = new Date(segmentStartDate);
        dayStart.setHours(0, 0, 0, 0);
        const dayEnd = new Date(dayStart);
        dayEnd.setHours(23, 59, 59, 999);
        
        const startTotalHours = isContinuation
          ? 0
          : Math.max(
              (segmentStartDate.getTime() - dayStart.getTime()) / (1000 * 60 * 60),
              0
            );
        leftValue = startTotalHours * cellOneHourWidth;

        widthValue = durationHoursExact * cellOneHourWidth;
        
        // Ограничиваем ширину, чтобы событие не выходило за границы ячейки (как для Schedule событий)
        const maxWidth = cellWidth - leftValue;
        widthValue = Math.min(widthValue, maxWidth);

        const currentDate = new Date(segmentStartDate);
        const dayKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;

        const dayEvents = calendarEvents.filter((candidateEvent) => {
          const candidateTs =
            (candidateEvent as any).extendedProps?.segmentStartTs ??
            (candidateEvent.start instanceof Date
              ? candidateEvent.start.getTime()
              : new Date(candidateEvent.start).getTime());

          if (!Number.isFinite(candidateTs)) {
            return false;
          }

          const candidateDate = new Date(candidateTs as number);
          const candidateKey = `${candidateDate.getFullYear()}-${candidateDate.getMonth()}-${candidateDate.getDate()}`;
          return candidateKey === dayKey;
        }).sort((a, b) => {
          // Сортируем события по времени начала для правильного определения порядка
          const aTs = (a as any).extendedProps?.segmentStartTs ??
            (a.start instanceof Date ? a.start.getTime() : new Date(a.start).getTime());
          const bTs = (b as any).extendedProps?.segmentStartTs ??
            (b.start instanceof Date ? b.start.getTime() : new Date(b.start).getTime());
          return aTs - bTs;
        });

        const eventIndex = dayEvents.findIndex(event => event.id === eventInfo.event.id);

        const dayCellHeight = getDayCellHeight();
        const eventHeight = dayCellHeight ? dayCellHeight / 5 : 20;
        // Первое событие должно быть с top: 0, второе с отступом от первого и т.д.
        topValue = eventIndex >= 0 ? eventIndex * eventHeight : 0;

        const styleValue = `left: ${leftValue}px; width: ${widthValue}px; top: ${topValue}px; height: ${eventHeight}px; background: ${backgroundColor}; border-left: 3px solid ${borderColor};`;

        return {
          html: `
            <div class="fc-custom-event" style="${styleValue}">
              <div class="fc-event-text">
                ${eventName}
              </div>
            </div>
          `
        };
      }
      
      // Fallback для month view, если cellWidth не определен
      return {
        html: `
          <div class="fc-custom-event" style="background: ${backgroundColor}; border-left: 3px solid ${borderColor};">
            <div class="fc-event-text">
              ${eventName}
            </div>
          </div>
        `
      };
    }

    // Оригинальная логика для Schedule событий
    // Проверяем, что это действительно Schedule событие, а не фиктивное для Event Calendar
    // Фиктивные события имеют eventType: 0 и пустой plane.id
    if (!planeEvent) {
      return { html: '' };
    }
    
    // Если есть eventCalendar, значит это событие Event Calendar, не используем planeEvent
    if (eventInfo.event.extendedProps.event) {
      return { html: '' };
    }

    const plane = (planeEvent as any).plane as IPlaneShort | undefined;

    // Получаем тип события
    const eventTypeText = planeEvent.eventType === ScheduleType.EMPTY_LEG
      ? intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)
      : planeEvent.eventType === ScheduleType.ORDER_REQUEST
        ? intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)
        : planeEvent.eventType === ScheduleType.CUSTOM
          ? intl.formatMessage(CommonFormMessages.eventTypeCustomRadio)
          : planeEvent.eventType === ScheduleType.MAINTENANCE
            ? intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio)
            : planeEvent.eventType === ScheduleType.OTHER
              ? intl.formatMessage(CommonFormMessages.eventTypeParkingRadio)
              : intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio);
    
    // Формируем название: [номер самолёта] - [тип события] - [название события, если есть]
    const titleParts = [
      plane?.registrationNumber ?? '—',
      eventTypeText
    ];
    
    // Добавляем название события, если оно есть
    if (planeEvent.eventName) {
      titleParts.push(planeEvent.eventName);
    }
    
    const title = titleParts.join(' - ');

    let eventTypeAttr = 'default';
    if (planeEvent.eventType === ScheduleType.EMPTY_LEG) {
      eventTypeAttr = 'empty_leg';
    } else if (planeEvent.eventType === ScheduleType.ORDER_REQUEST) {
      eventTypeAttr = 'order_request';
    } else if (planeEvent.eventType === ScheduleType.CUSTOM) {
      eventTypeAttr = 'custom';
    } else if (planeEvent.eventType === ScheduleType.MAINTENANCE) {
      eventTypeAttr = 'maintenance';
    } else if (planeEvent.eventType === ScheduleType.OTHER) {
      eventTypeAttr = 'other';
    }

    // Empty leg events are now clickable, so we don't need the non-clickable class
    const nonClickableClass = '';

      if (viewMode === 'week') {
        // Находим индекс самолета для позиционирования
        // Используем resourcesListForTimeline - тот же список, что используется для создания ресурсов
        // Это гарантирует соответствие между порядком ресурсов в FullCalendar и расчетом индекса
        let planeIndex = -1;
        let topOffset = 0;

        if (plane && plane.id) {
          // Ищем индекс самолета в списке по его id
          // Используем resourcesListForTimeline, чтобы порядок совпадал с порядком ресурсов
          planeIndex = resourcesListForTimeline.findIndex(p => p.id === plane.id);
          
          if (planeIndex !== -1) {
            // Рассчитываем topOffset: первый самолет (index 0) = 0px, второй (index 1) = 102px, третий (index 2) = 204px и т.д.
            // Используем значение 102px, которое соответствует высоте строки ресурса в FullCalendar
            topOffset = planeIndex * 102;
          }
        }

        // Используем data-plane-index для CSS позиционирования
        // Не устанавливаем top в inline стиле, так как FullCalendar сам позиционирует события в строке ресурса
        // CSS будет использовать data-plane-index для правильного позиционирования
        return {
          html: `
            <div class="fc-custom-event fc-custom-event--timeline ${nonClickableClass}" data-event-type="${eventTypeAttr}" data-plane-index="${planeIndex}" style="height: 20.4px; min-height: 20.4px;">
              <div class="fc-event-text">
                ${title}
              </div>
            </div>
          `,
        };
      }

    if (viewMode === 'year') {
      // Используем сегмент события для правильного расчета позиции в year view
      // Аналогично month view, используем локальное время
      const segmentStartTs = eventInfo.event.extendedProps.segmentStartTs as number | undefined;
      const segmentEndTs = eventInfo.event.extendedProps.segmentEndTs as number | undefined;

      // Парсим даты события как UTC
      const baseStart = planeEvent.dateFrom instanceof Date 
        ? planeEvent.dateFrom 
        : typeof planeEvent.dateFrom === 'string'
          ? parseStringAsUTCDate(planeEvent.dateFrom)
          : new Date(planeEvent.dateFrom);
      const baseEnd = planeEvent.dateTo instanceof Date
        ? planeEvent.dateTo
        : typeof planeEvent.dateTo === 'string'
          ? parseStringAsUTCDate(planeEvent.dateTo)
          : new Date(planeEvent.dateTo);

      const segmentStartDate = Number.isFinite(segmentStartTs) ? new Date(segmentStartTs as number) : baseStart;
      const segmentEndDate = Number.isFinite(segmentEndTs) ? new Date(segmentEndTs as number) : baseEnd;

      const currentDate = new Date(eventInfo.event.start);
      const dayStart = new Date(currentDate);
      dayStart.setHours(0, 0, 0, 0);
      const dayEnd = new Date(dayStart);
      dayEnd.setHours(23, 59, 59, 999);

      // Фильтруем события, которые перекрывают текущий день (как для Event Calendar)
      const dayEvents = calendarEvents.filter(event => {
        const eventStart = new Date(event.start);
        const eventEnd = new Date(event.end || eventStart);
        
        // Событие перекрывает день, если оно начинается до конца дня И заканчивается после начала дня
        return eventStart <= dayEnd && eventEnd >= dayStart;
      }).sort((a, b) => {
        // Сортируем по времени начала события
        const aStart = new Date(a.start).getTime();
        const bStart = new Date(b.start).getTime();
        return aStart - bStart;
      });

      const eventIndex = dayEvents.findIndex(event => event.id === eventInfo.event.id);
      
      // Ограничиваем отображение до 3 событий в день для режима year
      if (eventIndex >= 3) {
        return { html: '<div style="display: none;"></div>' };
      }

      const cellWidth = getDayCellWidth();
      let leftValue = 0;
      let widthValue = 0;

      if (cellWidth) {
        // Границы текущего дня в локальном времени
        const currentDayStart = new Date(currentDate);
        currentDayStart.setHours(0, 0, 0, 0);
        const currentDayEnd = new Date(currentDayStart);
        currentDayEnd.setHours(23, 59, 59, 999);

        // Вычисляем позицию начала события в текущем дне (в локальном времени)
        const dayStartTime = currentDayStart.getTime();
        const startTotalHours = Math.max(
          (segmentStartDate.getTime() - dayStartTime) / (1000 * 60 * 60),
          0
        );

        // Используем ту же логику нормализации, что и раньше, но с локальным временем
        const cellOneHourWidth = cellWidth / 24;
        leftValue = startTotalHours * cellOneHourWidth;

        // Вычисляем ширину события на основе длительности сегмента
        const durationMs = segmentEndDate.getTime() - segmentStartDate.getTime();
        const durationHoursExact = Math.max(durationMs / (1000 * 60 * 60), 0);
        widthValue = durationHoursExact * cellOneHourWidth;

        // Ограничиваем ширину, чтобы событие не выходило за границы ячейки дня
        const maxWidth = cellWidth - leftValue;
        widthValue = Math.min(widthValue, maxWidth);
      }

      const dayCellHeight = getDayCellHeight();
      const eventHeight = dayCellHeight ? dayCellHeight / 5 : 12;
      const topValue = eventIndex * eventHeight;

      const styleValue = `left: ${leftValue}px; width: ${widthValue}px; top: ${topValue}px; height: ${eventHeight}px;`;

      return {
        html: `
          <div class="fc-custom-event fc-year-event ${nonClickableClass}" data-event-type="${eventTypeAttr}" style="${styleValue}">
            <div class="fc-event-text">
              ${title}
            </div>
          </div>
        `
      };
    }

    const segmentStartTs = eventInfo.event.extendedProps.segmentStartTs as number | undefined;
    const segmentEndTs = eventInfo.event.extendedProps.segmentEndTs as number | undefined;

    // Парсим даты события как UTC
    const baseStart = typeof planeEvent.dateFrom === 'string'
      ? parseStringAsUTCDate(planeEvent.dateFrom)
      : (planeEvent.dateFrom && typeof planeEvent.dateFrom === 'object' && 'getTime' in planeEvent.dateFrom)
        ? planeEvent.dateFrom as Date
        : new Date(planeEvent.dateFrom as any);
    const baseEnd = typeof planeEvent.dateTo === 'string'
      ? parseStringAsUTCDate(planeEvent.dateTo)
      : (planeEvent.dateTo && typeof planeEvent.dateTo === 'object' && 'getTime' in planeEvent.dateTo)
        ? planeEvent.dateTo as Date
        : new Date(planeEvent.dateTo as any);

    const segmentStartDate = Number.isFinite(segmentStartTs) ? new Date(segmentStartTs as number) : baseStart;
    const segmentEndDate = Number.isFinite(segmentEndTs) ? new Date(segmentEndTs as number) : baseEnd;

    const durationMs = segmentEndDate.getTime() - segmentStartDate.getTime();
    const durationHoursExact = Math.max(durationMs / (1000 * 60 * 60), 0);
    const cellWidth = getDayCellWidth();
    let leftValue = 0;
    let widthValue = 0;
    let topValue = 0;

    if (cellWidth) {
      const cellOneHourWidth = cellWidth / 24;
      const segmentIndex = typeof eventInfo.event.extendedProps.segmentIndex === 'number'
        ? eventInfo.event.extendedProps.segmentIndex
        : 0;
      const isContinuation = segmentIndex > 0 || segmentStartDate.getTime() > baseStart.getTime();

      const dayStart = new Date(segmentStartDate);
      dayStart.setHours(0, 0, 0, 0);
      const startTotalHours = isContinuation
        ? 0
        : Math.max(
            (segmentStartDate.getTime() - dayStart.getTime()) / (1000 * 60 * 60),
            0
          );
      leftValue = startTotalHours * cellOneHourWidth;

      widthValue = durationHoursExact * cellOneHourWidth;

      const currentDate = new Date(segmentStartDate);
      const dayKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;

      const dayEvents = calendarEvents.filter((candidateEvent) => {
        const candidateTs =
          (candidateEvent as any).extendedProps?.segmentStartTs ??
          (candidateEvent.start instanceof Date
            ? candidateEvent.start.getTime()
            : new Date(candidateEvent.start).getTime());

        if (!Number.isFinite(candidateTs)) {
          return false;
        }

        const candidateDate = new Date(candidateTs as number);
        const candidateKey = `${candidateDate.getFullYear()}-${candidateDate.getMonth()}-${candidateDate.getDate()}`;
        return candidateKey === dayKey;
      }).sort((a, b) => {
        // Сортируем события по времени начала для правильного определения порядка
        const aTs = (a as any).extendedProps?.segmentStartTs ??
          (a.start instanceof Date ? a.start.getTime() : new Date(a.start).getTime());
        const bTs = (b as any).extendedProps?.segmentStartTs ??
          (b.start instanceof Date ? b.start.getTime() : new Date(b.start).getTime());
        return aTs - bTs;
      });

      const eventIndex = dayEvents.findIndex(event => event.id === eventInfo.event.id);

      const dayCellHeight = getDayCellHeight();
      const eventHeight = dayCellHeight ? dayCellHeight / 5 : 20;
      // Первое событие должно быть с top: 0, второе с отступом от первого и т.д.
      topValue = eventIndex >= 0 ? eventIndex * eventHeight : 0;

      const styleValue = `left: ${leftValue}px; width: ${widthValue}px; top: ${topValue}px; height: ${eventHeight}px;`;

      return {
        html: `
          <div class="fc-custom-event ${nonClickableClass}" data-event-type="${eventTypeAttr}" style="${styleValue}">
            <div class="fc-event-text">
              ${title}
            </div>
          </div>
        `
      };
    }
  }, [getDayCellWidth, getDayCellHeight, calendarEvents, viewMode, intl, planeList, planeListProp, resourcesListForTimeline]);

  const eventTooltip = (eventInfo: any) => {
    const planeEvent = eventInfo.event.extendedProps.planeEvent;

    // Получаем тип события
    const eventTypeText = planeEvent.eventType === ScheduleType.EMPTY_LEG
      ? intl.formatMessage(CommonFormMessages.eventTypeEmptyLegRadio)
      : planeEvent.eventType === ScheduleType.ORDER_REQUEST
        ? intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio)
        : planeEvent.eventType === ScheduleType.CUSTOM
          ? intl.formatMessage(CommonFormMessages.eventTypeCustomRadio)
          : planeEvent.eventType === ScheduleType.MAINTENANCE
            ? intl.formatMessage(CommonFormMessages.eventTypeMaintenanceRadio)
            : planeEvent.eventType === ScheduleType.OTHER
              ? intl.formatMessage(CommonFormMessages.eventTypeParkingRadio)
              : intl.formatMessage(CommonFormMessages.eventTypeOrderRequestRadio);

    return `
      <div style="padding: 10px; max-width: 300px;">
        <div style="font-weight: bold; margin-bottom: 8px;">${planeEvent.planeRegistrationNumber}</div>
        <div style="margin-bottom: 4px;">
          <strong>${intl.formatMessage(FullCalendarWrapperMessages.event)}:</strong> ${eventTypeText}
        </div>
        ${planeEvent.airportFrom && planeEvent.airportTo ? `
          <div style="margin-bottom: 4px;">
            <strong>${intl.formatMessage(FullCalendarWrapperMessages.route)}:</strong> 
            ${DisplayRoute([planeEvent.airportFrom, planeEvent.airportTo])}
          </div>
        ` : ''}
        <div style="margin-bottom: 4px;">
          <strong>${intl.formatMessage(FullCalendarWrapperMessages.period)}:</strong> 
          ${formatDate(planeEvent.dateFrom, 0)} - ${formatDate(planeEvent.dateTo, 0)}
        </div>
      </div>
    `;
  };

  // Функция для получения номеров недель из DOM
  const getWeekNumbers = () => {
    if (!calendarRef.current) return [];

    // Ищем все элементы с номерами недель в календаре
    const weekNumberElements = document.querySelectorAll('.fc-daygrid-week-number');

    // Если элементы не найдены, возвращаем пустой массив
    if (weekNumberElements.length === 0) {
      return [];
    }

    const weeks: Array<{number: number, start: Date, end: Date}> = [];

    // Очищаем массив перед добавлением новых значений
    weeks.length = 0;

    weekNumberElements.forEach((element) => {
      const weekNumber = parseInt(element.textContent || '0');

      if (weekNumber > 0) {
        // Получаем родительскую строку недели через parentElement
        let weekRow = element.parentElement;
        let depth = 0;
        while (weekRow && !weekRow.classList.contains('fc-daygrid-week') && depth < 10) {
          weekRow = weekRow.parentElement;
          depth++;
        }

        if (weekRow) {
          // Получаем первый день недели
          const firstDay = weekRow.querySelector('.fc-daygrid-day');

          if (firstDay) {
            const dayEl = firstDay as HTMLElement;
            const dateAttr = dayEl.getAttribute('data-date');

            if (dateAttr) {
              const startDate = new Date(dateAttr);
              const endDate = new Date(startDate.getTime() + 6 * 24 * 60 * 60 * 1000);

              weeks.push({
                number: weekNumber,
                start: startDate,
                end: endDate
              });
            }
          }
        }
      }
    });

    return weeks;
  };


  const [weekNumbers, setWeekNumbers] = useState<Array<{number: number, start: Date, end: Date}>>([]);

  const resolveFullCalendarView = useCallback((mode: ViewMode) => {
    switch (mode) {
      case 'year':
        return 'multiMonthYear';
      case 'week':
        return 'timelineWeek';
      default:
        return initialView;
    }
  }, [initialView]);

  // Переключаем view при изменении режима
  useEffect(() => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      const newView = resolveFullCalendarView(viewMode);
      calendar.changeView(newView);
    }
  }, [resolveFullCalendarView, viewMode]);

  // Обновляем номера недель при изменении даты
  useEffect(() => {
    if (viewMode !== 'month') {
      setWeekNumbers([]);
      return;
    }

    const updateWeekNumbers = () => {
      // Сначала очищаем состояние
      setWeekNumbers([]);

      // Небольшая задержка, чтобы DOM успел обновиться
      setTimeout(() => {
        if (calendarRef.current) {
          const weeks = getWeekNumbers();
          setWeekNumbers(weeks);
        }
      }, 200);
    };

    updateWeekNumbers();

    // Обновляем при изменении даты календаря
    const calendar = calendarRef.current?.getApi();
    if (calendar) {
      calendar.on('datesSet', updateWeekNumbers);
      calendar.on('viewDidMount' as any, updateWeekNumbers);
    }
    let activeCalendar = calendar;

    return () => {
      if (activeCalendar) {
        activeCalendar.off('datesSet', updateWeekNumbers);
        activeCalendar.off('viewDidMount' as any, updateWeekNumbers);
      }
    };
  }, [initialDate, viewMode]);

  useEffect(() => {
    setViewMode(resolvedInitialViewMode);
  }, [resolvedInitialViewMode]);

  // Единая функция для подсчета скрытых событий для дня
  const getHiddenEventsCount = useCallback((dateStr: string): number => {
    if (!calendarEvents || calendarEvents.length === 0) {
      return 0;
    }

    const dayStart = new Date(dateStr);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setHours(23, 59, 59, 999);

    // Фильтруем события, которые перекрывают текущий день
    const dayEvents = calendarEvents.filter(event => {
      const eventStart = new Date(event.start);
      const eventEnd = new Date(event.end || eventStart);
      // Событие перекрывает день, если оно начинается до конца дня И заканчивается после начала дня
      return eventStart <= dayEnd && eventEnd >= dayStart;
    });

    // Считаем скрытые события: всего событий минус 3 видимых
    return dayEvents.length > 3 ? dayEvents.length - 3 : 0;
  }, [calendarEvents]);

  // Функция для создания элементов "+X more" в режиме year
  const setupYearMoreLinks = useCallback(() => {
    if (viewMode !== 'year' || !calendarEvents || calendarEvents.length === 0) {
      // При переключении с year на другой режим удаляем все year-more-link
      document.querySelectorAll('.fc-year-more-link').forEach(el => el.remove());
      return;
    }

    // Удаляем старые элементы "+X more" (на случай повторного вызова)
    document.querySelectorAll('.fc-year-more-link').forEach(el => el.remove());

    // Находим все ячейки дней
    let dayCells = document.querySelectorAll('.fc-multiMonthYear-view .fc-daygrid-day');
    if (dayCells.length === 0) {
      dayCells = document.querySelectorAll('.fc-daygrid-day');
    }

    dayCells.forEach((dayCell) => {
      const dayEl = dayCell as HTMLElement;
      const dateStr = dayEl.dataset.date;
      if (!dateStr) return;

      // Используем единую функцию для подсчета скрытых событий
      const hiddenEventsCount = getHiddenEventsCount(dateStr);

      // Если событий больше 3, создаем элемент "+X more"
      if (hiddenEventsCount > 0) {
        const dayEventsContainer = dayEl.querySelector('.fc-daygrid-day-events') as HTMLElement;
        if (!dayEventsContainer) return;

        // Убеждаемся, что контейнер имеет position: relative
        const containerStyle = window.getComputedStyle(dayEventsContainer);
        if (containerStyle.position === 'static') {
          dayEventsContainer.style.position = 'relative';
        }

        // Убеждаемся, что родительский элемент не обрезает содержимое
        const dayCellStyle = window.getComputedStyle(dayEl);
        if (dayCellStyle.overflow === 'hidden') {
          dayEl.style.overflow = 'visible';
        }
        if (dayCellStyle.overflowY === 'hidden') {
          dayEl.style.overflowY = 'visible';
        }

        // Фиксированное позиционирование в конце дня
        const topValue = 35;

        // Проверяем, не создан ли уже элемент
        const existingMoreLink = dayEventsContainer.querySelector('.fc-year-more-link');
        if (existingMoreLink) {
          const expectedText = `+${hiddenEventsCount} ${intl.formatMessage({id: "app.more", defaultMessage: "more"})}`;
          // Обновляем только если текст изменился
          if ((existingMoreLink as HTMLElement).textContent !== expectedText) {
            (existingMoreLink as HTMLElement).textContent = expectedText;
          }
          (existingMoreLink as HTMLElement).style.top = `${topValue}px`;
          return;
        }
        
        // Проверяем, нет ли уже текста "+X more" в контейнере (на случай, если FullCalendar создал свой элемент)
        const allTextNodes = Array.from(dayEventsContainer.childNodes).filter(node => 
          node.nodeType === Node.TEXT_NODE && 
          node.textContent?.trim().startsWith('+') && 
          node.textContent?.includes('more')
        );
        if (allTextNodes.length > 0) {
          // Удаляем текстовые узлы, которые могут быть дубликатами
          allTextNodes.forEach(node => node.remove());
        }

        const moreLink = document.createElement('div');
        moreLink.className = 'fc-year-more-link';
        const moreText = `+${hiddenEventsCount} ${intl.formatMessage({id: "app.more", defaultMessage: "more"})}`;
        moreLink.textContent = moreText;
        moreLink.style.cssText = `
          position: absolute;
          top: ${topValue}px;
          left: 2px;
          right: 2px;
          font-size: 8px;
          font-weight: 600;
          color: #333;
          padding: 1px 2px;
          cursor: default;
          pointer-events: none;
          user-select: none;
          z-index: 1000;
          line-height: 1.2;
          white-space: nowrap;
          overflow: visible;
        `;

        // Убеждаемся, что контейнер не скрывает элемент
        if (containerStyle.overflow === 'hidden') {
          dayEventsContainer.style.overflow = 'visible';
        }
        if (containerStyle.overflowY === 'hidden') {
          dayEventsContainer.style.overflowY = 'visible';
        }

        dayEventsContainer.appendChild(moreLink);
      }
    });
  }, [viewMode, calendarEvents, intl, getHiddenEventsCount]);

  // Вызываем функцию при изменении viewMode или calendarEvents
  useEffect(() => {
    if (viewMode === 'year') {
      // Используем requestAnimationFrame и несколько попыток для надежности
      requestAnimationFrame(() => {
        setTimeout(() => {
          setupYearMoreLinks();
          // Повторяем еще раз через небольшую задержку
          setTimeout(setupYearMoreLinks, 200);
        }, 200);
      });
    }
  }, [viewMode, calendarEvents, setupYearMoreLinks]);

  const showViewModeToggle = resolvedViewModes.length > 1;

  const viewModeButtonLabel = (mode: ViewMode) => {
    switch (mode) {
      case 'month':
        return intl.formatMessage({ id: "eventCalendarPage.month", defaultMessage: "Месяц" });
      case 'year':
        return intl.formatMessage({ id: "eventCalendarPage.year", defaultMessage: "Год" });
      case 'week':
        return intl.formatMessage({ id: "eventCalendarPage.week", defaultMessage: "Неделя" });
      default:
        return mode;
    }
  };

  // Generate view mode toggle content for header left
  const viewModeToggleContent = useMemo(() => {
    if (!showViewModeToggle) return undefined;
    
    return (
      <div className="fc-view-mode-toggle">
        {resolvedViewModes.map((mode) => (
          <button
            key={mode}
            className={`fc-view-mode-button ${viewMode === mode ? 'active' : ''}`}
            onClick={() => setViewMode(mode)}
          >
            {viewModeButtonLabel(mode)}
          </button>
        ))}
      </div>
    );
  }, [showViewModeToggle, resolvedViewModes, viewMode, viewModeButtonLabel]);

  // Use headerLeftContent prop if provided, otherwise use view mode toggle
  const effectiveHeaderLeftContent = headerLeftContent !== undefined ? headerLeftContent : viewModeToggleContent;

  // Inject header left content into calendar toolbar
  const headerLeftContentRef = useRef<Root | null>(null);
  const leftContainerRef = useRef<HTMLElement | null>(null);
  
  const injectLeftHeaderContent = useCallback(() => {
    if (!effectiveHeaderLeftContent || !calendarRef.current) return;
    
    const calendarApi = calendarRef.current.getApi();
    const calendarEl = (calendarApi as any).el as HTMLElement | undefined;
    if (!calendarEl) return;

    const toolbar = calendarEl.querySelector('.fc-header-toolbar.fc-toolbar') as HTMLElement | null;
    if (!toolbar) return;

    const leftChunk = toolbar.querySelector('.fc-toolbar-chunk:first-child') as HTMLElement | null;
    if (!leftChunk) return;

    // Use existing container or create once
    let container = leftContainerRef.current || leftChunk.querySelector('.fc-header-left-content') as HTMLElement | null;
    if (!container) {
      container = document.createElement('div');
      container.className = 'fc-header-left-content';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      leftChunk.insertBefore(container, leftChunk.firstChild);
      leftContainerRef.current = container;
    }

    // Render React component into container
    if (!headerLeftContentRef.current) {
      headerLeftContentRef.current = createRoot(container);
    }
    headerLeftContentRef.current.render(<>{effectiveHeaderLeftContent}</>);
  }, [effectiveHeaderLeftContent]);

  useEffect(() => {
    if (!effectiveHeaderLeftContent) {
      // Clean up if content was removed
      if (headerLeftContentRef.current) {
        headerLeftContentRef.current.unmount();
        headerLeftContentRef.current = null;
      }
      leftContainerRef.current = null;
      return;
    }

    // Only inject when content changes, not on every render
    const timeoutId = setTimeout(() => {
      injectLeftHeaderContent();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [effectiveHeaderLeftContent, injectLeftHeaderContent]);

  // Inject header right content into calendar toolbar
  const headerRightContentRef = useRef<Root | null>(null);
  const rightContainerRef = useRef<HTMLElement | null>(null);
  
  const injectRightHeaderContent = useCallback(() => {
    if (!headerRightContent || !calendarRef.current) return;
    
    const calendarApi = calendarRef.current.getApi();
    const calendarEl = (calendarApi as any).el as HTMLElement | undefined;
    if (!calendarEl) return;

    const toolbar = calendarEl.querySelector('.fc-header-toolbar.fc-toolbar') as HTMLElement | null;
    if (!toolbar) return;

    const rightChunk = toolbar.querySelector('.fc-toolbar-chunk:last-child') as HTMLElement | null;
    if (!rightChunk) return;

    // Use existing container or create once
    let container = rightContainerRef.current || rightChunk.querySelector('.fc-header-right-content') as HTMLElement | null;
    if (!container) {
      container = document.createElement('div');
      container.className = 'fc-header-right-content';
      container.style.display = 'flex';
      container.style.alignItems = 'center';
      rightChunk.appendChild(container);
      rightContainerRef.current = container;
    }

    // Render React component into container
    if (!headerRightContentRef.current) {
      headerRightContentRef.current = createRoot(container);
    }
    headerRightContentRef.current.render(<>{headerRightContent}</>);
  }, [headerRightContent]);

  useEffect(() => {
    if (!headerRightContent) {
      // Clean up if content was removed
      if (headerRightContentRef.current) {
        headerRightContentRef.current.unmount();
        headerRightContentRef.current = null;
      }
      rightContainerRef.current = null;
      return;
    }

    // Only inject when content changes, not on every render
    const timeoutId = setTimeout(() => {
      injectRightHeaderContent();
    }, 0);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [headerRightContent, injectRightHeaderContent]);

  const buildTimeSlotsMarkup = useCallback((mode: ViewMode) => {
    const isMobile = width <= 768;
    
    if (mode === 'month' || mode === 'week') {
      if (isMobile) {
        // В мобильном режиме показываем только 00 и 16
        return `
          <span class="fc-time-slot">00</span>
          <span class="fc-time-slot">16</span>
        `;
      }
      return `
          <span class="fc-time-slot">00</span>
          <span class="fc-time-slot">04</span>
          <span class="fc-time-slot">08</span>
          <span class="fc-time-slot">12</span>
          <span class="fc-time-slot">16</span>
          <span class="fc-time-slot">20</span>
        `;
    }

    if (mode === 'year') {
      if (isMobile) {
        // В мобильном режиме показываем только 00 и 16
        return `
          <span class="fc-time-slot">00</span>
          <span class="fc-time-slot">16</span>
        `;
      }
      return `
          <span class="fc-time-slot">00</span>
          <span class="fc-time-slot">08</span>
          <span class="fc-time-slot">16</span>
        `;
    }

    return '';
  }, [width]);

  const buildDayHeaderContent = useCallback((date: Date, mode: ViewMode) => {
    const dayOfWeekRaw = date.toLocaleDateString(intl.locale, { weekday: 'short' });
    const dayOfWeek = dayOfWeekRaw.toUpperCase();
    const dayOfMonth = date.getDate();
    const fullDate = `${String(dayOfMonth).padStart(2, '0')}.${String(date.getMonth() + 1).padStart(2, '0')}.${date.getFullYear()}`;
    const timeSlots = buildTimeSlotsMarkup(mode);

    const timeSlotsSection = timeSlots
      ? `<div class="fc-day-time-slots">
            ${timeSlots}
         </div>`
      : '';

    return {
      html: `
        <div class="fc-day-header-content">
          <div class="fc-day-header-main">
            ${
              mode === 'week'
                ? `<div class="fc-day-weekday">${dayOfWeek} ${fullDate}</div>`
                : `<div class="fc-day-weekday">${dayOfWeek}</div>`
            }
          </div>
          ${timeSlotsSection}
        </div>
      `
    };
  }, [buildTimeSlotsMarkup, intl.locale]);

  const calendarSpecificProps: Record<string, unknown> = useMemo(() => {
    const props: Record<string, unknown> = {};

    if (viewMode === 'week') {
      props.slotDuration = { hours: 1 };
      props.slotLabelInterval = { hours: 2 };
      props.slotLabelFormat = [
        { weekday: 'short', day: 'numeric' },
        { hour: '2-digit', minute: '2-digit' }
      ];
      props.slotWidth = 6;
      props.scrollTime = '00:00';
      props.slotLabelContent = (arg: any) => {
        if (arg.level === 0) {
          return buildDayHeaderContent(arg.date, 'week');
        }

        return {
          html: `<span class="fc-timeline-time-slot">${arg.text}</span>`
        };
      };
      props.resourceAreaHeaderContent = intl.formatMessage(FullCalendarWrapperMessages.resourceAreaTitle);
      props.resourceAreaWidth = width <= 768 ? '60px' : '140px';
      props.resourceLabelContent = (arg: any) => ({
        html: `<div class="fc-plane-resource">${arg.resource.title}</div>`,
      });
      props.expandRows = true;
    } else {
      props.dayHeaderFormat = {
        weekday: 'short',
        day: 'numeric'
      };

      props.dayHeaderContent = (arg: any) => buildDayHeaderContent(arg.date, viewMode);
    }

    return props;
  }, [buildDayHeaderContent, intl, viewMode, width]);

  const resolvedInitialView = useMemo(() => resolveFullCalendarView(viewMode), [resolveFullCalendarView, viewMode]);

  const calendarWrapperClassName = useMemo(
    () => (viewMode === 'week' ? 'fc-calendar-wrapper fc-calendar-wrapper--timeline' : 'fc-calendar-wrapper'),
    [viewMode]
  );

  const calendarWrapperStyle = useMemo(() => {
    if (viewMode === 'month') {
      return { padding: width <= 768 ? '0 20px' : '8px 40px' };
    }

    if (viewMode === 'week') {
      return { padding: '0px' };
    }

    return {};
  }, [viewMode, width]);

  return (
    <div style={{ height: height, position: 'relative' }}>
      {/* Номера недель слева */}
      {weekNumbers.length > 0 && viewMode === 'month' && (
        <div className="fc-week-numbers-container fc-week-numbers-left">
          {weekNumbers.map((week: {number: number, start: Date, end: Date}, index: number) => (
            <div key={index} className="fc-week-number-item">
              {week.number}
            </div>
          ))}
        </div>
      )}
      
      {/* Номера недель справа */}
      {weekNumbers.length > 0 && viewMode === 'month' && (
        <div className="fc-week-numbers-container fc-week-numbers-right">
          {weekNumbers.map((week: {number: number, start: Date, end: Date}, index: number) => (
            <div key={`right-${index}`} className="fc-week-number-item">
              {week.number}
            </div>
          ))}
        </div>
      )}
      
      {/* Календарь */}
      <div className={calendarWrapperClassName} style={calendarWrapperStyle}>
        <div className="fc-calendar-inner">
        <FullCalendar
        ref={calendarRef}
        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin, resourceTimelinePlugin]}
        initialView={resolvedInitialView}
        initialDate={initialDate}
        events={calendarEvents}
        resources={viewMode === 'week' ? timelineResources : undefined}
        eventClick={handleEventClick}
        select={handleDateSelect}
        eventContent={eventContent}
        eventMouseEnter={handleEventMouseEnter}
        eventMouseLeave={handleEventMouseLeave}
        // eventTooltip={eventTooltip}
        headerToolbar={{
          left: '',
          center: 'prev,title,next',
          right: ''
        }}
        buttonText={{
          today: 'today',
          month: 'month',
          week: intl.formatMessage(CommonDetailsMessages.week),
          day: intl.formatMessage(CommonDetailsMessages.day),
        }}
        locale="en"
        datesSet={(arg) => {
          // Вызываем callback для обновления видимого диапазона дат
          if (onDatesChange) {
            const start = new Date(arg.start);
            start.setHours(0, 0, 0, 0);
            
            // end в FullCalendar - это первый день после видимого диапазона, поэтому вычитаем 1 мс
            const end = new Date(arg.end.getTime() - 1);
            end.setHours(23, 59, 59, 999);
            
            onDatesChange(start, end);
          }

          const calendarApi = calendarRef.current?.getApi();
          const calendarEl = calendarApi ? (calendarApi as any).el as HTMLElement | undefined : undefined;

          if (arg.view.type === 'timelineWeek') {
            const currentStart = arg.start;
            const currentEnd = new Date(arg.end.getTime() - 1);

            const startDay = String(currentStart.getDate()).padStart(2, '0');
            const endDay = String(currentEnd.getDate()).padStart(2, '0');
            const monthName = currentStart.toLocaleDateString(intl.locale, { month: 'long' });
            const monthWithDay = currentStart.toLocaleDateString(intl.locale, { month: 'long', day: 'numeric' });
            const year = currentStart.getFullYear();

            const titleMonth = monthName.charAt(0).toUpperCase() + monthName.slice(1);
            const title = `${startDay} - ${endDay} ${titleMonth}`;

            const monthGenitiveRaw = monthWithDay.replace(/\d+/g, '').replace(/[.,]/g, '').trim();
            const monthGenitive = monthGenitiveRaw.charAt(0).toUpperCase() + monthGenitiveRaw.slice(1);

            // const titleEl = calendarEl?.querySelector('.fc-toolbar-title');
            // if (titleEl) {
            //   titleEl.textContent = title;
            // }

            const toolbar = calendarEl ? calendarEl.querySelector('.fc-header-toolbar.fc-toolbar') : null;
            if (toolbar) {
              let monthBadge = toolbar.querySelector('.fc-toolbar-month') as HTMLDivElement | null;
              if (!monthBadge) {
                monthBadge = document.createElement('div');
                monthBadge.className = 'fc-toolbar-month';
                toolbar.insertBefore(monthBadge, toolbar.firstChild);
              }
              monthBadge.textContent = `${monthGenitive} ${year}`;
            }
          } else {
            // const titleEl = calendarEl ? calendarEl.querySelector('.fc-toolbar-title') : null;
            // if (titleEl) {
            //   titleEl.textContent = arg.view.title;
            // }

            // const monthBadge = calendarEl ? calendarEl.querySelector('.fc-toolbar-month') : null;
            // if (monthBadge && monthBadge.parentElement) {
            //   monthBadge.parentElement.removeChild(monthBadge);
            // }
          }
        }}
        // Кастомное форматирование заголовка без "г."
        viewDidMount={(view) => {
          // Inject header content when view is mounted
          injectLeftHeaderContent();
          injectRightHeaderContent();
          
          // Добавляем обработчики ховера на "more link" элементы для Event Calendar
          const setupMoreLinkHovers = () => {
            // Проверяем тип view из календаря
            const currentViewType = view.view.type;
            const isYearView = viewMode === 'year' || currentViewType === 'multiMonthYear';
            
            // Для режима "year" используем отдельную функцию
            if (isYearView) {
              setupYearMoreLinks();
            } else {
              // Для режима "month" используем стандартные .fc-more-link
              // Удаляем все year-more-link при переключении на month view
              document.querySelectorAll('.fc-year-more-link').forEach(el => el.remove());
              
              const moreLinks = document.querySelectorAll('.fc-more-link');
              moreLinks.forEach((link) => {
                const linkEl = link as HTMLElement;
                
                // Проверяем, есть ли уже обработчик
                if (linkEl.dataset.hoverHandlerAdded === 'true') {
                  // Обновляем текст, если он уже был обработан (на случай изменения количества событий)
                  const dayEl = linkEl.closest('.fc-daygrid-day');
                  if (dayEl) {
                    const dateStr = (dayEl as HTMLElement).dataset.date;
                    if (dateStr) {
                      const hiddenEventsCount = getHiddenEventsCount(dateStr);
                      if (hiddenEventsCount > 0) {
                        const expectedText = `+${hiddenEventsCount} ${intl.formatMessage({id: "app.more", defaultMessage: "more"})}`;
                        // Обновляем только если текст изменился
                        if (linkEl.textContent !== expectedText) {
                          linkEl.textContent = expectedText;
                        }
                      }
                    }
                  }
                  return;
                }
                
                linkEl.dataset.hoverHandlerAdded = 'true';
                
                // Получаем дату из атрибута или из родительского элемента
                const dayEl = linkEl.closest('.fc-daygrid-day');
                if (!dayEl) return;
                
                const dateStr = (dayEl as HTMLElement).dataset.date;
                if (!dateStr) return;
                
                // Используем единую функцию для подсчета скрытых событий
                const hiddenEventsCount = getHiddenEventsCount(dateStr);
                
                // Обновляем текст "more link" на "+X more" для всех событий (унифицированный формат)
                if (hiddenEventsCount > 0) {
                  const expectedText = `+${hiddenEventsCount} ${intl.formatMessage({id: "app.more", defaultMessage: "more"})}`;
                  // Проверяем, не установлен ли уже правильный текст
                  if (linkEl.textContent !== expectedText) {
                    linkEl.textContent = expectedText;
                  }
                  
                  // Позиционируем текст на месте четвертого события (top: 90px)
                  // Согласно CSS: первое событие top: 0, второе top: 30px, третье top: 60px, четвертое top: 90px
                  // Текст должен быть на месте четвертого события, то есть под третьим
                  linkEl.style.top = '47px';
                  linkEl.style.position = 'absolute';
                  
                  // Полностью отключаем клик и все интерактивные элементы
                  linkEl.style.pointerEvents = 'none';
                  linkEl.style.cursor = 'default';
                  linkEl.style.userSelect = 'none';
                  
                  // Удаляем все обработчики событий
                  const newLinkEl = linkEl.cloneNode(true) as HTMLElement;
                  linkEl.parentNode?.replaceChild(newLinkEl, linkEl);
                  
                  // Блокируем все возможные клики на новом элементе
                  newLinkEl.addEventListener('click', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }, true);
                  
                  newLinkEl.addEventListener('mousedown', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }, true);
                  
                  newLinkEl.addEventListener('mouseup', (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    e.stopImmediatePropagation();
                    return false;
                  }, true);
                }
              });
            }
          };
          
          // Устанавливаем обработчики после рендера
          // Для режима "year" используем requestAnimationFrame для более надежного вызова
          const setupWithDelay = () => {
            if (viewMode === 'year' || view.view.type === 'multiMonthYear') {
              // Для режима year используем несколько попыток с задержкой
              requestAnimationFrame(() => {
                setTimeout(() => {
                  setupMoreLinkHovers();
                  // Повторяем еще раз через небольшую задержку для надежности
                  setTimeout(setupMoreLinkHovers, 100);
                }, 200);
              });
            } else {
              setTimeout(setupMoreLinkHovers, 100);
            }
          };
          
          setupWithDelay();
          
          // Обновляем обработчики при изменении дат
          view.view.calendar.on('datesSet', () => {
            setupWithDelay();
          });
          
          // Обновляем плашки при изменении событий
          const updateMoreLinks = () => {
            setupWithDelay(); // Это обновит обработчики ховера
            const currentViewType = view.view.type;
            if (viewMode === 'year' || currentViewType === 'multiMonthYear') {
              setupYearMoreLinks();
            } else {
              // Обновляем плашки для month view
              // Удаляем все year-more-link при переключении на month view
              document.querySelectorAll('.fc-year-more-link').forEach(el => el.remove());
              
              const moreLinks = document.querySelectorAll('.fc-more-link');
              moreLinks.forEach((link) => {
                const linkEl = link as HTMLElement;
                const dayEl = linkEl.closest('.fc-daygrid-day');
                if (!dayEl) return;
                
                const dateStr = (dayEl as HTMLElement).dataset.date;
                if (!dateStr) return;
                
                const hiddenEventsCount = getHiddenEventsCount(dateStr);
                if (hiddenEventsCount > 0) {
                  const expectedText = `+${hiddenEventsCount} ${intl.formatMessage({id: "app.more", defaultMessage: "more"})}`;
                  // Обновляем только если текст изменился
                  if (linkEl.textContent !== expectedText) {
                    linkEl.textContent = expectedText;
                  }
                }
              });
            }
          };
          
          view.view.calendar.on('eventsSet', updateMoreLinks);
        }}
        height={height}
        allDaySlot={false}
        nowIndicator={true}
        schedulerLicenseKey={viewMode === 'week' ? 'GPL-My-Project-Is-Open-Source' : undefined}
        editable={false}
        selectable={true}
        selectMirror={true}
        weekends={true}
        firstDay={firstDayOfWeek} // Настраиваемый первый день недели
        // Показываем номера недель
        weekNumbers={viewMode === 'month'}
        weekNumberFormat={{
          week: 'numeric'
        }}
        // Настройки для отображения событий (максимум 3 в день для month и year view)
        dayMaxEventRows={false}
        moreLinkClick={() => {}} // Пустая функция, чтобы отключить поповер
        dayMaxEvents={(viewMode === 'month' || viewMode === 'year') ? 3 : false}
        moreLinkContent={(arg) => {
          // Используем стандартный текст, кастомизацию делаем через обработчик ховера
          // где у нас есть доступ к DOM элементу и дате
          return arg.text;
        }}
        {...calendarSpecificProps}
      />
        </div>
      </div>
      
      {/* Popover для события */}
      {selectedEventForPopover && popoverAnchorEl && (
        <EventPopover
          event={selectedEventForPopover}
          visible={popoverVisible}
          onVisibleChange={(visible) => {
            if (!visible) {
              hidePopover();
            }
          }}
        >
          <div ref={popoverAnchorRef} />
        </EventPopover>
      )}
      
    </div>
  );
};
